import type { PagedSourceConfig, ReportColumn } from '../types';
import { selectFilter } from '../utils';

/**
 * Columns that only mean something after the missing-data run. Ticking any of these (or their B
 * twins, which follow automatically) asks the user to press Start before Export is enabled.
 *
 * ⚠️ On the list, firstName / surname / email are the APPLICANT's. On a sign-up made for someone else
 * the people attending are different, so these only become real after the run.
 */
export const INTAKE_ENRICHED_KEYS = new Set([
  'firstName', 'surname', 'email', 'phone', 'gender', 'dateOfBirth', 'missingFields', 'filledFrom',
]);

/** Person B duplicates. Same order as the Person A columns; no checkbox of their own. */
const twinColumns: ReportColumn[] = [
  { key: 'firstNameB', label: 'First Name (B)', category: 'Person' },
  { key: 'surnameB', label: 'Surname (B)', category: 'Person' },
  { key: 'emailB', label: 'Email (B)', category: 'Person' },
  { key: 'phoneB', label: 'Phone (B)', category: 'Person' },
  { key: 'genderB', label: 'Gender (B)', category: 'Person' },
  { key: 'dateOfBirthB', label: 'Date of Birth (B)', category: 'Person', type: 'date' },
];

export const intake: PagedSourceConfig = {
  mode: 'paged',
  label: 'Intake',
  plural: 'Intakes',
  columns: [
    { key: 'reference', label: 'Reference', category: 'Intake', defaultVisible: true },
    { key: 'intakeID', label: 'ID', category: 'Intake' },
    // ⚠️ From the list this is the applicant's name, not necessarily who is attending.
    { key: 'fullName', label: 'Applicant Full Name', category: 'Person', defaultVisible: true },
    { key: 'firstName', label: 'First Name', category: 'Person', twinKey: 'firstNameB' },
    { key: 'surname', label: 'Surname', category: 'Person', twinKey: 'surnameB' },
    // No longer a default: it now means the attending person's email, which needs the run.
    { key: 'email', label: 'Email', category: 'Person', twinKey: 'emailB' },
    { key: 'phone', label: 'Phone', category: 'Person', twinKey: 'phoneB' },
    { key: 'gender', label: 'Gender', category: 'Person', twinKey: 'genderB' },
    { key: 'dateOfBirth', label: 'Date of Birth', category: 'Person', type: 'date', twinKey: 'dateOfBirthB' },
    { key: 'courseName', label: 'Course', category: 'Intake', defaultVisible: true },
    { key: 'startDisplay', label: 'Intake Starts', category: 'Intake', defaultVisible: true },
    { key: 'locationName', label: 'Location', category: 'Church', defaultVisible: true },
    { key: 'statusName', label: 'Status', category: 'Intake', defaultVisible: true },
    { key: 'participantCount', label: 'People', category: 'Intake' },
    { key: 'submittedDateTime', label: 'Submitted', category: 'Intake', type: 'datetime', defaultVisible: true },
    { key: 'ageDisplay', label: 'Age', category: 'Intake' },
    { key: 'mainPersonAffiliation', label: 'Affiliation', category: 'Person' },
    { key: 'isExistingMember', label: 'Existing Member', category: 'Person' },
    { key: 'missingFields', label: 'Missing fields', category: 'Data quality', trailing: true },
    { key: 'filledFrom', label: 'Filled from', category: 'Data quality', trailing: true },
  ],
  twinColumns,
  filters: [
    selectFilter('status', 'Status', 'All statuses'),
    selectFilter('course', 'Course', 'All courses', undefined, 'courseID'),
    selectFilter('location', 'Location', 'All locations', undefined, 'locationID'),
    { key: 'submittedFrom', field: 'submittedDateTime', label: 'Submitted from', type: 'date', op: 'gte', placeholder: 'Any date' },
    { key: 'submittedTo', field: 'submittedDateTime', label: 'Submitted to', type: 'date', op: 'lte', placeholder: 'Any date' },
  ],
};
